interface FilterSidebarProps {
  category: 'apparel' | 'footwear' | 'accessories';
  selectedSubcategory: string;
  setSelectedSubcategory: (value: string) => void;
  selectedSizes: string[];
  setSelectedSizes: (value: string[]) => void;
  selectedColors: string[];
  setSelectedColors: (value: string[]) => void;
  priceRange: [number, number];
  setPriceRange: (value: [number, number]) => void;
}

export default function FilterSidebar({
  category,
  selectedSizes,
  setSelectedSizes,
  selectedColors,
  setSelectedColors,
  priceRange,
  setPriceRange,
}: FilterSidebarProps) {
  const getSizeOptions = () => {
    if (category === 'apparel') {
      return ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
    } else if (category === 'footwear') {
      return ['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13'];
    } else {
      return ['One Size', 'S/M', 'L/XL'];
    }
  };

  const colorOptions = [
    { name: 'Black', value: 'black', hex: '#000000' },
    { name: 'White', value: 'white', hex: '#FFFFFF' },
    { name: 'Gray', value: 'gray', hex: '#6B7280' },
    { name: 'Red', value: 'red', hex: '#DC2626' },
    { name: 'Navy', value: 'navy', hex: '#1E3A8A' },
    { name: 'Green', value: 'green', hex: '#059669' },
  ];

  const toggleSize = (size: string) => {
    if (selectedSizes.includes(size)) {
      setSelectedSizes(selectedSizes.filter((s) => s !== size));
    } else {
      setSelectedSizes([...selectedSizes, size]);
    }
  };

  const toggleColor = (color: string) => {
    if (selectedColors.includes(color)) {
      setSelectedColors(selectedColors.filter((c) => c !== color));
    } else {
      setSelectedColors([...selectedColors, color]);
    }
  };

  const clearAllFilters = () => {
    setSelectedSizes([]);
    setSelectedColors([]);
    setPriceRange([0, 500]);
  };

  return (
    <div className="space-y-8">
      {/* Clear Filters */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white tracking-wide">FILTERS</h3>
        <button
          onClick={clearAllFilters}
          className="text-xs text-gray-400 hover:text-white transition-colors"
        >
          Clear All
        </button>
      </div>

      {/* Size Filter */}
      <div>
        <h4 className="text-sm font-bold text-white mb-4">SIZE</h4>
        <div className="grid grid-cols-3 gap-2">
          {getSizeOptions().map((size) => (
            <button
              key={size}
              onClick={() => toggleSize(size)}
              className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                selectedSizes.includes(size)
                  ? 'bg-white text-black'
                  : 'bg-[#1A1A1A] text-gray-400 hover:text-white hover:border-white/20 border border-transparent'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Color Filter */}
      <div>
        <h4 className="text-sm font-bold text-white mb-4">COLOR</h4>
        <div className="space-y-3">
          {colorOptions.map((color) => (
            <button
              key={color.value}
              onClick={() => toggleColor(color.value)}
              className="w-full flex items-center gap-3 group"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  selectedColors.includes(color.value)
                    ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0A]'
                    : 'ring-1 ring-white/20 group-hover:ring-white/40'
                }`}
                style={{ backgroundColor: color.hex }}
              >
                {selectedColors.includes(color.value) && (
                  <i className={`ri-check-line text-lg ${color.value === 'white' ? 'text-black' : 'text-white'}`}></i>
                )}
              </div>
              <span className={`text-sm font-medium transition-colors ${
                selectedColors.includes(color.value) ? 'text-white' : 'text-gray-400 group-hover:text-white'
              }`}>
                {color.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Price Range Filter */}
      <div>
        <h4 className="text-sm font-bold text-white mb-4">PRICE RANGE</h4>
        <div className="space-y-4">
          <input
            type="range"
            min="0"
            max="500"
            step="10"
            value={priceRange[1]}
            onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
            className="w-full h-2 bg-[#1A1A1A] rounded-lg appearance-none cursor-pointer accent-white"
          />
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">${priceRange[0]}</span>
            <span className="text-white font-bold">${priceRange[1]}</span>
          </div>
        </div>
      </div>

      {/* Style Filter */}
      <div>
        <h4 className="text-sm font-bold text-white mb-4">STYLE</h4>
        <div className="space-y-2">
          {['Streetwear', 'Classic', 'Premium', 'Limited'].map((style) => (
            <label key={style} className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="w-5 h-5 rounded bg-[#1A1A1A] border-white/20 text-white focus:ring-white focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-gray-400 group-hover:text-white transition-colors">
                {style}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}